import { PhotosByEvent, DatabasePhoto as Photo, PhotoAudience, Season, Event, ViewerMode } from '../types';
import { API_BASE_PATHS, ApiBasePath, buildMediaUrl } from './mediaUrl';
import { throwMappedError } from './httpErrors';

// The per-id photo endpoint accepts exactly one of these fields.
export type PhotoPatch = { caption: string } | { audience: PhotoAudience };

// GET /auth-session names the credential that admitted the request.
interface SessionStatus {
  credentialMode: ViewerMode;
}

class ApiClient {
  private baseURL: ApiBasePath;
  private readonly csrfHeaderName = 'X-YS-CSRF';
  private readonly csrfHeaderValue = '1';
  private unauthenticatedCallback: (() => void) | null = null;
  private notifyingUnauthenticated = false;

  constructor() {
    const isDev = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test';
    const hasNetlifyDev = window.location.port === '8888' || window.location.port === '8887';

    if (isDev && hasNetlifyDev) {
      // Running with netlify dev - use direct function URLs as redirects may not work
      this.baseURL = API_BASE_PATHS.functions;
    } else if (isDev) {
      // Running npm start without netlify dev or in test environment - warn user
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️  Running without Netlify Functions. Use "npm run dev" for full functionality.');
      }
      this.baseURL = API_BASE_PATHS.functions;
    } else {
      this.baseURL = API_BASE_PATHS.production; // Use redirects in production
    }
  }

  mediaUrl(photoId: number): string {
    return buildMediaUrl(this.baseURL, photoId);
  }

  setUnauthenticatedCallback(callback: (() => void) | null): void {
    this.unauthenticatedCallback = callback;
  }

  /**
   * fetch with same-origin credentials by default; a 401 notifies the
   * unauthenticated callback once per microtask burst. Returns the raw response.
   */
  private async request(input: string, options?: RequestInit): Promise<Response> {
    let response: Response;
    if (options) {
      const init: RequestInit = { ...options, credentials: options.credentials ?? 'same-origin' };
      response = await fetch(input, init);
    } else {
      response = await fetch(input);
    }

    const shouldNotifyUnauthenticated = response.status === 401
      && !options?.signal?.aborted
      && !this.notifyingUnauthenticated;
    if (shouldNotifyUnauthenticated) {
      this.notifyingUnauthenticated = true;
      this.unauthenticatedCallback?.();
      queueMicrotask(() => {
        this.notifyingUnauthenticated = false;
      });
    }

    return response;
  }

  private async getJson<T>(
    path: string,
    message: (status: number) => string,
    invalidJsonMessage?: string,
    signal?: AbortSignal
  ): Promise<T> {
    const response = await this.request(`${this.baseURL}${path}`, signal ? { signal } : undefined);
    if (!response.ok) {
      await throwMappedError(response, { default: message(response.status) });
    }
    try {
      return await response.json();
    } catch (error) {
      if (error instanceof SyntaxError && invalidJsonMessage) {
        throw new Error(invalidJsonMessage);
      }
      throw error;
    }
  }

  private getMutationHeaders(headers: HeadersInit = {}): HeadersInit {
    return {
      ...headers,
      [this.csrfHeaderName]: this.csrfHeaderValue
    };
  }

  private getMutationOptions(options: RequestInit = {}): RequestInit {
    return {
      ...options,
      credentials: 'same-origin',
      headers: this.getMutationHeaders(options.headers)
    };
  }

  async login(password: string): Promise<boolean> {
    const response = await this.request(`${this.baseURL}/auth-login`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });

    if (response.status === 401) {
      return false;
    }

    if (!response.ok) {
      await throwMappedError(response, { default: (body) => body.error || 'Login failed' });
    }

    return true;
  }

  // Every read names its mode explicitly; the server clamps it against the
  // credential, so this can narrow but never widen.
  async getAllPhotos(mode: ViewerMode, signal?: AbortSignal): Promise<PhotosByEvent> {
    return this.getJson<PhotosByEvent>(`/photos?mode=${mode}`, (status) => `HTTP ${status}: Failed to fetch photos`, undefined, signal);
  }

  async fetchMediaBlob(photoId: number, signal?: AbortSignal): Promise<Blob> {
    const response = await this.request(this.mediaUrl(photoId), { signal });
    if (!response.ok) {
      const message = `HTTP ${response.status}: Failed to fetch media`;
      await throwMappedError(response, { default: message });
    }
    return response.blob();
  }

  async getSessionStatus(signal?: AbortSignal): Promise<ViewerMode> {
    const status = await this.getJson<SessionStatus>('/auth-session', () => 'Failed to restore session', undefined, signal);
    return status.credentialMode;
  }

  async logout(): Promise<void> {
    const response = await this.request(`${this.baseURL}/auth-session`, this.getMutationOptions({ method: 'DELETE' }));
    if (!response.ok && response.status !== 401) {
      await throwMappedError(response, { default: 'Sign out failed' });
    }
  }

  async uploadPhoto(eventName: string, month: string, seasonId: string, file: File, caption?: string): Promise<Photo> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('eventName', eventName);
    formData.append('month', month);
    formData.append('seasonId', seasonId);

    if (caption) {
      formData.append('caption', caption);
    }

    const response = await this.request(`${this.baseURL}/photos`, this.getMutationOptions({
      method: 'POST',
      body: formData,
    }));

    if (!response.ok) {
      await throwMappedError(response, {
        default: (d) => `Upload failed: ${d.error || 'Unknown server error'}`,
      });
    }
    return response.json();
  }

  // Per-id route: photos.ts reads the id from the path segment
  async updatePhoto(id: number, patch: PhotoPatch): Promise<Photo> {
    const response = await this.request(`${this.baseURL}/photos/${id}`, this.getMutationOptions({
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }));

    if (!response.ok) {
      await throwMappedError(response, { default: 'Failed to update photo' });
    }
    return response.json();
  }

  async deletePhoto(id: number): Promise<void> {
    const response = await this.request(`${this.baseURL}/photos/${id}`, this.getMutationOptions({
      method: 'DELETE',
    }));

    if (!response.ok) {
      await throwMappedError(response, { default: 'Failed to delete photo' });
    }
  }

  async reorderPhotos(eventName: string, photoIds: number[]): Promise<Photo[]> {
    const response = await this.request(`${this.baseURL}/photos/reorder`, this.getMutationOptions({
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ eventName, photoIds }),
    }));

    if (!response.ok) {
      await throwMappedError(response, { default: 'Failed to reorder photos' });
    }
    const { photos } = await response.json() as { photos: Photo[] };
    return photos;
  }

  async deleteEvent(eventId: number): Promise<void> {
    if (!Number.isInteger(eventId) || eventId <= 0) {
      throw new Error('A positive integer event ID is required');
    }

    const response = await this.request(`${this.baseURL}/events?id=${eventId}`, this.getMutationOptions({
      method: 'DELETE',
      headers: {
        'X-Request-ID': `event-delete-${eventId}-${Date.now()}`
      },
    }));

    if (!response.ok) {
      const photoConflictResolver = (d: { error?: string }) =>
        d.error?.includes('Cannot delete event with photos')
          ? 'Cannot delete event: it has associated photos. Please delete photos first.'
          : d.error || 'Cannot delete event';

      await throwMappedError(response, {
        404: 'Event not found. It may have already been deleted.',
        409: photoConflictResolver,
        400: photoConflictResolver,
        default: (d) => d.error || `Failed to delete event (${response.status})`,
      });
    }
  }

  async createEvent(name: string, month: string, seasonId: string): Promise<Event> {
    const response = await this.request(`${this.baseURL}/events`, this.getMutationOptions({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, month, seasonId }),
    }));

    if (!response.ok) {
      await throwMappedError(response, {
        409: 'An event with this name already exists',
        400: (d) => d.error || 'Invalid event data',
        default: (d) => d.error || 'Failed to create event',
      });
    }
    return response.json();
  }

  async updateEvent(id: number, updates: { name?: string; month?: string }): Promise<Event> {
    if (!id || id <= 0) {
      throw new Error('Invalid event ID provided');
    }

    if (!updates || (updates.name === undefined && updates.month === undefined)) {
      throw new Error('No updates provided');
    }

    if (updates.name !== undefined && !updates.name.trim()) {
      throw new Error('Event name cannot be empty');
    }

    const response = await this.request(`${this.baseURL}/events?id=${id}`, this.getMutationOptions({
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        // Add request correlation ID for debugging
        'X-Request-ID': `event-update-${id}-${Date.now()}`
      },
      body: JSON.stringify(updates),
    }));

    if (!response.ok) {
      await throwMappedError(response, {
        400: (d) => d.error || 'Invalid event data provided',
        404: 'Event not found. It may have been deleted by another user.',
        409: (d) => d.error || 'An event with this name already exists',
        500: 'Server error occurred. Please try again.',
        503: 'Service temporarily unavailable. Please try again in a moment.',
        429: 'Too many requests. Please wait a moment before trying again.',
        default: (d) => `Unexpected error: ${d.error || 'Please try again'}`,
      });
    }

    let eventData: any;
    try {
      eventData = await response.json();
    } catch (parseError) {
      console.error('Failed to parse successful response:', parseError);
      throw new Error('Server returned invalid response format');
    }

    this.assertEventShape(eventData);

    return eventData;
  }

  private assertEventShape(eventData: any): void {
    if (!eventData || typeof eventData !== 'object') {
      throw new Error('Server returned invalid event data');
    }

    if (!eventData.id || !eventData.name || !eventData.month) {
      console.error('Server returned incomplete event data:', eventData);
      throw new Error('Server returned incomplete event information');
    }
  }

  async getAllSeasons(): Promise<Season[]> {
    return this.getJson<Season[]>('/seasons', (status) => `HTTP ${status}: Failed to fetch seasons`, 'API returned invalid response - ensure Netlify Functions are running');
  }

  async getEventsBySeason(seasonId: string) {
    return this.getJson<any>(`/seasons?id=${seasonId}`, (status) => status === 404 ? `Season ${seasonId} not found` : `HTTP ${status}: Failed to fetch events for season ${seasonId}`);
  }

  async createSeason(name: string, year: number): Promise<Season> {
    const response = await this.request(`${this.baseURL}/seasons`, this.getMutationOptions({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, year }),
    }));

    if (!response.ok) {
      await throwMappedError(response, {
        409: `Season ${name} ${year} already exists`,
        400: (d) => d.error || 'Invalid season data',
        default: (d) => d.error || 'Failed to create season',
      });
    }
    return response.json();
  }

  async deleteSeason(seasonId: string): Promise<void> {
    const response = await this.request(`${this.baseURL}/seasons?id=${seasonId}`, this.getMutationOptions({
      method: 'DELETE',
    }));

    if (!response.ok) {
      await throwMappedError(response, {
        400: (d) => d.error || 'Cannot delete season with events',
        404: `Season ${seasonId} not found`,
        default: (d) => d.error || 'Failed to delete season',
      });
    }
  }

}

export const apiClient = new ApiClient();
